import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Target, ShieldAlert, Plus, Pencil, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { useCan } from '@/lib/permissions';

const PROGRESS_STATUS = {
  achieved: { label: 'Achieved', cls: 'text-emerald-300', bar: 'bg-emerald-400' },
  on_track: { label: 'On track', cls: 'text-emerald-300', bar: 'bg-emerald-400' },
  at_risk: { label: 'At risk', cls: 'text-amber-300', bar: 'bg-amber-400' },
  off_track: { label: 'Off track', cls: 'text-rose-300', bar: 'bg-rose-400' },
  no_data: { label: 'No reading yet', cls: 'text-muted-foreground', bar: 'bg-muted' },
};

function Sparkline({ points, testId }) {
  if (!points || points.length < 2) return null;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const w = 110, h = 24, n = points.length;
  const coords = points.map((p, i) => [
    (i / (n - 1)) * (w - 4) + 2,
    h - 2 - ((p.value - min) / range) * (h - 4),
  ]);
  const d = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' ');
  const last = coords[coords.length - 1];
  return (
    <svg width={w} height={h} className="text-primary" data-testid={testId}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill="currentColor" />
    </svg>
  );
}

function GoalProgress({ o, canWrite, onSave, onRefresh }) {
  const [val, setVal] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = o.progress || {};
  const st = PROGRESS_STATUS[p.status] || PROGRESS_STATUS.no_data;
  const linked = !!o.metric_id;
  return (
    <div className="mt-3 pt-3 border-t border-border/30" data-testid={`goal-progress-${o.objective_id}`}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-muted-foreground">{p.metric_name || o.metric || 'Progress'}{p.as_of ? ` · as of ${p.as_of}` : ''}{p.source === 'auto' ? ' · auto' : ''}</span>
        <span className={`font-semibold ${st.cls}`} data-testid={`goal-status-${o.objective_id}`}>{st.label}{p.percent != null ? ` · ${p.percent}%` : ''}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted/50 overflow-hidden">
        <div className={`h-full ${st.bar} transition-all`} style={{ width: `${p.percent != null ? p.percent : 0}%` }} />
      </div>
      {p.baseline != null ? (
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
          <span>{p.baseline}</span>
          <span>{p.current != null ? `now ${p.current}` : 'no reading'}</span>
          <span>{p.target != null ? p.target : (o.target_value || '—')}</span>
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground mt-1">Add a baseline and a current reading to track progress.</p>
      )}
      {p.history?.length >= 2 && (
        <div className="mt-2 flex items-center gap-2">
          <Sparkline points={p.history} testId={`goal-sparkline-${o.objective_id}`} />
          <span className="text-[10px] text-muted-foreground">{p.history.length} readings</span>
        </div>
      )}
      {canWrite && (
        open ? (
          <div className="flex gap-1.5 mt-2">
            <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Current value (e.g. 97.5%)" className="h-8 text-xs" data-testid={`goal-reading-input-${o.objective_id}`} />
            <Button size="sm" className="h-8" disabled={!val.trim()} onClick={() => { onSave(val.trim()); setVal(''); setOpen(false); }} data-testid={`goal-reading-save-${o.objective_id}`}>Save</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center gap-3 mt-2">
            {linked && (
              <button disabled={busy} onClick={async () => { setBusy(true); try { await onRefresh(); } finally { setBusy(false); } }} data-testid={`goal-reading-refresh-${o.objective_id}`} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh from certified metric
              </button>
            )}
            <button onClick={() => setOpen(true)} data-testid={`goal-reading-update-${o.objective_id}`} className="text-[11px] text-muted-foreground hover:underline">Record manually</button>
          </div>
        )
      )}
    </div>
  );
}

const empty = () => ({ name: '', description: '', kind: 'objective', direction: 'increase', weight: 3, target_label: '', applies_to: [], metric: '', metric_id: '', metric_name: '', metric_model_id: '', metric_source: '', baseline: '', target_value: '', target_period: '', business_owner: '', level: 'warning', threshold: '', response: '' });

export default function ObjectivesPage() {
  const canWrite = useCan('measures:certify');
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ directions: [], kinds: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listObjectives();
      setItems(r.objectives || []);
    } catch (e) { toast.error(e.message || 'Could not load objectives'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.getObjectivesOptions().then(setOptions).catch(() => {});
    api.getCertifiedMetrics().then((r) => setMetrics(r.metrics || [])).catch(() => {}); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty()); setDialogOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ name: o.name || '', description: o.description || '', kind: o.kind || 'objective',
      direction: o.direction || 'increase', weight: o.weight ?? 3, target_label: o.target_label || '',
      applies_to: o.applies_to || [], metric: o.metric || '', baseline: o.baseline || '',
      target_value: o.target_value || '', target_period: o.target_period || '', business_owner: o.business_owner || '',
      metric_id: o.metric_id || '', metric_name: o.metric_name || '',
      metric_model_id: o.metric_model_id || '', metric_source: o.metric_source || '',
      level: o.level || 'warning', threshold: o.threshold || '', response: o.response || '' });
    setDialogOpen(true);
  };
  const toggleCat = (c) => setForm((p) => ({ ...p, applies_to: p.applies_to.includes(c) ? p.applies_to.filter((x) => x !== c) : [...p.applies_to, c] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('A name is required'); return; }
    setSaving(true);
    try {
      if (editing) await api.updateObjective(editing.objective_id, form);
      else await api.createObjective(form);
      toast.success(editing ? 'Objective updated' : 'Objective created');
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const remove = async (o) => {
    try { await api.deleteObjective(o.objective_id); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.message || 'Could not remove'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="objectives-page">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Target className="w-6 h-6 text-primary" /> Goals and Guardrails</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Goals are the business results you want to achieve (baseline → target by a period); guardrails are
            limits that must be protected. Recommendations touching a business area are aligned to these and shown
            inline — a required-control guardrail forces independent approval. Deterministic; no customer data.
          </p>
        </div>
        {canWrite && <Button onClick={openNew} data-testid="objective-new-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="objectives-empty">
          No objectives yet. Add strategic objectives (e.g. "Improve combined ratio") and guardrails (e.g. "Don't grow reserves risk") to steer recommendation ranking.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="objectives-list">
          {items.map((o) => {
            const guard = o.kind === 'guardrail';
            return (
              <div key={o.objective_id} className="bg-card/60 border border-border/40 rounded-2xl p-4" data-testid={`objective-card-${o.objective_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-heading font-semibold text-foreground flex items-center gap-1.5">
                    {guard ? <ShieldAlert className="w-4 h-4 text-rose-500" /> : <Target className="w-4 h-4 text-primary" />}
                    {o.name}
                  </div>
                  {canWrite && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`objective-edit-${o.objective_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(o)} data-testid={`objective-delete-${o.objective_id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                    </div>
                  )}
                </div>
                {o.description && <p className="text-xs text-muted-foreground mt-1">{o.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                  <span className={`px-1.5 py-0.5 rounded-md border ${guard ? 'text-rose-600 border-rose-500/30 bg-rose-500/10' : 'text-primary border-primary/30 bg-primary/10'}`}>{guard ? 'Guardrail' : 'Goal'}</span>
                  {guard && <span className={`px-1.5 py-0.5 rounded-md border ${o.level === 'required_control' ? 'text-rose-600 border-rose-500/40 bg-rose-500/15' : 'text-amber-600 border-amber-500/30 bg-amber-500/10'}`}>{o.level === 'required_control' ? 'Required control' : 'Warning'}</span>}
                  <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground capitalize">{o.direction} {o.metric || o.target_label || ''}</span>
                  {!guard && (o.baseline || o.target_value) && <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground">{o.baseline || '—'} → {o.target_value || o.target_label || '—'}{o.target_period ? ` by ${o.target_period}` : ''}</span>}
                  {!guard && o.business_owner && <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground">{o.business_owner}</span>}
                  {!guard && <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground">weight {o.weight}</span>}
                  {guard && o.threshold && <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground">threshold: {o.threshold}</span>}
                  {(o.applies_to || []).map((c) => <span key={c} className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground capitalize">{c}</span>)}
                </div>
                {!guard && o.progress && (
                  <GoalProgress o={o} canWrite={canWrite}
                    onSave={async (val) => { try { await api.recordGoalReading(o.objective_id, val); toast.success('Reading recorded'); load(); } catch (e) { toast.error(e.message); } }}
                    onRefresh={async () => { try { const r = await api.refreshGoalReading(o.objective_id); toast.success(`Refreshed: ${(r.progress || {}).current ?? 'updated'}`); load(); } catch (e) { toast.error(e.message || 'Could not refresh'); } }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="objective-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} objective / guardrail</DialogTitle>
            <DialogDescription>Recommendations touching a selected business area are aligned to this.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Improve combined ratio" data-testid="objective-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional" data-testid="objective-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Type</Label>
                <select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="objective-kind">
                  {(options.kinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <Label>Direction</Label>
                <select value={form.direction} onChange={(e) => setForm((p) => ({ ...p, direction: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="objective-direction">
                  {(options.directions || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label>Weight</Label>
                <Input type="number" min={1} max={5} value={form.weight} onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))} disabled={form.kind === 'guardrail'} data-testid="objective-weight" />
              </div>
            </div>
            <div>
              <Label>Target metric (display)</Label>
              <Input value={form.target_label} onChange={(e) => setForm((p) => ({ ...p, target_label: e.target.value }))} placeholder="e.g. Combined Ratio" data-testid="objective-target" />
            </div>
            {form.kind === 'objective' ? (
              <div className="rounded-lg border border-border/40 bg-muted/20 p-3 space-y-3" data-testid="goal-detail-fields">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Goal detail</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Metric</Label><Input value={form.metric} onChange={(e) => setForm((p) => ({ ...p, metric: e.target.value }))} placeholder="e.g. Combined ratio" data-testid="goal-metric" /></div>
                  <div><Label>Business owner</Label><Input value={form.business_owner} onChange={(e) => setForm((p) => ({ ...p, business_owner: e.target.value }))} placeholder="e.g. Pricing Leadership" data-testid="goal-owner" /></div>
                  <div><Label>Baseline / current</Label><Input value={form.baseline} onChange={(e) => setForm((p) => ({ ...p, baseline: e.target.value }))} placeholder="e.g. 101.2%" data-testid="goal-baseline" /></div>
                  <div><Label>Target value</Label><Input value={form.target_value} onChange={(e) => setForm((p) => ({ ...p, target_value: e.target.value }))} placeholder="e.g. below 95%" data-testid="goal-target-value" /></div>
                  <div className="col-span-2"><Label>Target period</Label><Input value={form.target_period} onChange={(e) => setForm((p) => ({ ...p, target_period: e.target.value }))} placeholder="e.g. Q3 2027" data-testid="goal-period" /></div>
                  <div className="col-span-2">
                    <Label>Certified metric (for live progress)</Label>
                    <select value={form.metric_id} onChange={(e) => { const m = metrics.find((x) => x.metric_id === e.target.value); setForm((p) => ({ ...p, metric_id: e.target.value, metric_name: m ? m.metric_name : '', metric_model_id: m ? (m.model_id || '') : '', metric_source: m ? (m.source || '') : '' })); }} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="goal-metric-link">
                      <option value="">Not linked</option>
                      {metrics.map((m) => <option key={m.metric_id} value={m.metric_id}>{m.metric_name}{m.executable ? ' (auto)' : ''}</option>)}
                    </select>
                    <p className="text-[11px] text-muted-foreground mt-1">Link a certified metric so progress can be auto-refreshed from a governed number. "(auto)" metrics run live on refresh.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 space-y-3" data-testid="guardrail-detail-fields">
                <p className="text-[11px] uppercase tracking-wider text-rose-300">Guardrail detail</p>
                <div>
                  <Label>Level</Label>
                  <select value={form.level} onChange={(e) => setForm((p) => ({ ...p, level: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="guardrail-level">
                    <option value="warning">Warning — may proceed after documenting risk</option>
                    <option value="required_control">Required control — needs independent approval to proceed</option>
                  </select>
                </div>
                <div><Label>Threshold</Label><Input value={form.threshold} onChange={(e) => setForm((p) => ({ ...p, threshold: e.target.value }))} placeholder="e.g. Loss ratio must not exceed 75%" data-testid="guardrail-threshold" /></div>
                <div><Label>Response if breached</Label><Input value={form.response} onChange={(e) => setForm((p) => ({ ...p, response: e.target.value }))} placeholder="e.g. Escalate to Chief Actuary before proceeding" data-testid="guardrail-response" /></div>
              </div>
            )}
            <div>
              <Label>Applies to business areas</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Recommendations in these categories are linked. Leave empty to apply to all.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="objective-applies">
                {(options.categories || []).map((c) => {
                  const on = form.applies_to.includes(c);
                  return (
                    <button key={c} onClick={() => toggleCat(c)} className={`text-[11px] px-2 py-1 rounded-full border capitalize transition-colors ${on ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`} data-testid={`objective-cat-${c}`}>{c}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="objective-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
